import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_RADIO_CONFIG,
  detectActiveScheduleSlot,
  detectStreamType,
  type RadioConfig,
  type RadioScheduleItem,
} from '../../../utils/radioConfig';

type Props = {
  config: RadioConfig;
  setConfig: React.Dispatch<React.SetStateAction<RadioConfig>>;
  loading: boolean;
  saving: boolean;
  onSave: () => Promise<boolean>;
  onReload: () => void;
};

function FieldHint({ children }: { children: React.ReactNode }) {
  return <p className="mt-1 text-xs leading-relaxed text-stone-500">{children}</p>;
}

function Section({
  step,
  title,
  badge,
  children,
}: {
  step: string;
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-900">
          {step}
        </span>
        <h3 className="text-sm font-bold text-stone-900">{title}</h3>
        {badge}
      </div>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

const STREAM_TYPE_LABELS: Record<string, { label: string; color: string; icon: string }> = {
  hls:        { label: 'HLS stream (.m3u8)', color: 'text-violet-700', icon: 'ri-signal-tower-line' },
  mp3:        { label: 'Direct audio file', color: 'text-emerald-700', icon: 'ri-music-2-line' },
  icecast:    { label: 'Icecast / Shoutcast', color: 'text-blue-700', icon: 'ri-radio-line' },
  youtube:    { label: 'YouTube — embeds in player', color: 'text-red-600', icon: 'ri-youtube-line' },
  soundcloud: { label: 'SoundCloud — embeds in player', color: 'text-orange-600', icon: 'ri-soundcloud-line' },
  unknown:    { label: 'Unrecognised URL type', color: 'text-amber-700', icon: 'ri-question-line' },
  none:       { label: '', color: '', icon: '' },
};

export default function AdminRadioPanel({
  config,
  setConfig,
  loading,
  saving,
  onSave,
  onReload,
}: Props) {
  const [previewScheduleOpen, setPreviewScheduleOpen] = useState(true);
  const [saveBanner, setSaveBanner] = useState<string | null>(null);
  const [savedConfig, setSavedConfig] = useState<string>(() => JSON.stringify(config));
  const [autoDetectResult, setAutoDetectResult] = useState<string | null>(null);

  // Track unsaved changes
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(config) !== savedConfig,
    [config, savedConfig]
  );

  // Debounced cover image preview
  const [previewImage, setPreviewImage] = useState(config.trackImage);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => setPreviewImage(config.trackImage), 600);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  }, [config.trackImage]);

  const scheduleMatchWarning = useMemo(() => {
    const titles = new Set(config.schedule.map((s) => s.show.trim()).filter(Boolean));
    const cur = config.currentShowTitle.trim();
    if (!cur || titles.size === 0) return null;
    if (!titles.has(cur)) {
      return `On-air title "${cur}" does not match any schedule row's Show name — the active slot won't highlight on the home card.`;
    }
    return null;
  }, [config.currentShowTitle, config.schedule]);

  const streamType = detectStreamType(config.streamUrl);
  const streamInfo = STREAM_TYPE_LABELS[streamType];

  function updateScheduleRow(index: number, patch: Partial<RadioScheduleItem>) {
    setConfig((prev) => {
      const schedule = [...prev.schedule];
      schedule[index] = { ...schedule[index], ...patch };
      return { ...prev, schedule };
    });
  }

  function addScheduleRow() {
    setConfig((prev) => ({
      ...prev,
      schedule: [...prev.schedule, { time: '', show: '', host: '' }],
    }));
  }

  function removeScheduleRow(index: number) {
    if (!window.confirm('Remove this schedule row?')) return;
    setConfig((prev) => ({
      ...prev,
      schedule: prev.schedule.filter((_, j) => j !== index),
    }));
  }

  function moveScheduleRow(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= config.schedule.length) return;
    setConfig((prev) => {
      const schedule = [...prev.schedule];
      [schedule[index], schedule[next]] = [schedule[next], schedule[index]];
      return { ...prev, schedule };
    });
  }

  function duplicateScheduleRow(index: number) {
    setConfig((prev) => {
      const copy = { ...prev.schedule[index] };
      const schedule = [...prev.schedule];
      schedule.splice(index + 1, 0, copy);
      return { ...prev, schedule };
    });
  }

  /** Copy the selected schedule row into the On-air fields. */
  function setAsCurrentShow(row: RadioScheduleItem) {
    setConfig((prev) => ({
      ...prev,
      currentShowTitle: row.show,
      currentShowSubtitle: `Hosted by ${row.host} · ${row.time}`,
    }));
  }

  /** Find the schedule slot that matches the current wall-clock time. */
  function autoDetectCurrentShow() {
    const idx = detectActiveScheduleSlot(config.schedule);
    if (idx === -1) {
      setAutoDetectResult('No schedule row matched the current time. Check that time ranges use format "HH:MM - HH:MM".');
      return;
    }
    const row = config.schedule[idx];
    setConfig((prev) => ({
      ...prev,
      currentShowTitle: row.show,
      currentShowSubtitle: `Hosted by ${row.host} · ${row.time}`,
    }));
    setAutoDetectResult(`Auto-filled from row ${idx + 1}: "${row.show}" (${row.time})`);
    setTimeout(() => setAutoDetectResult(null), 6000);
  }

  async function handleSaveClick() {
    setSaveBanner(null);
    const ok = await onSave();
    if (ok) {
      setSavedConfig(JSON.stringify(config));
      setSaveBanner('Saved — home page radio card updated immediately for new visitors.');
      window.setTimeout(() => setSaveBanner(null), 7000);
    }
  }

  const input =
    'w-full rounded-xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-900 shadow-sm placeholder:text-stone-400 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200/60';

  const lbl = 'block text-xs font-semibold uppercase tracking-wide text-stone-600';

  const currentShow = config.currentShowTitle;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl border border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-stone-50 p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-stone-900">Radio station settings</h2>
            <p className="mt-1 max-w-3xl text-sm text-stone-600">
              Controls the <strong className="text-stone-800">Live radio</strong> card on the homepage — station copy, broadcast status, stream URL, on-air show, demo track art, and the day schedule.
              Stored in <code className="rounded bg-amber-100/80 px-1.5 py-0.5 font-mono text-xs text-amber-950">app_settings.radio</code>.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50"
            >
              View homepage <i className="ri-external-link-line" />
            </a>
          </div>
        </div>
      </div>

      {/* Banners */}
      {saveBanner && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          <i className="ri-checkbox-circle-line text-lg text-emerald-600" />
          {saveBanner}
        </div>
      )}

      {hasUnsavedChanges && !saveBanner && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <i className="ri-edit-line shrink-0 text-amber-600" />
          You have unsaved changes. Click <strong className="mx-1">Save radio settings</strong> to publish them.
        </div>
      )}

      {scheduleMatchWarning && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <i className="ri-alarm-warning-line mt-0.5 shrink-0 text-amber-700" />
          <span>{scheduleMatchWarning}</span>
        </div>
      )}

      {autoDetectResult && (
        <div className="flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <i className="ri-time-line shrink-0 text-sky-600" />
          {autoDetectResult}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-stone-500">
          <i className="ri-loader-4-line animate-spin text-xl" />
          Loading radio settings…
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(300px,420px)] xl:grid-cols-[minmax(0,1.1fr)_minmax(320px,440px)]">
          {/* ── Editor column ── */}
          <div className="space-y-4">

            {/* 1 · Station identity */}
            <Section step="1" title="Station identity &amp; audience">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={lbl}>Station name</label>
                  <input
                    value={config.stationName}
                    onChange={(e) => setConfig((c) => ({ ...c, stationName: e.target.value }))}
                    className={`mt-1.5 ${input}`}
                    maxLength={200}
                    placeholder="Frequency Locus Radio One"
                  />
                  <FieldHint>The large headline at the top of the radio card.</FieldHint>
                </div>
                <div className="sm:col-span-2">
                  <label className={lbl}>Tagline</label>
                  <textarea
                    value={config.tagline}
                    onChange={(e) => setConfig((c) => ({ ...c, tagline: e.target.value }))}
                    className={`mt-1.5 min-h-[80px] resize-y ${input}`}
                    maxLength={2000}
                  />
                  <FieldHint>Supporting line under the station name.</FieldHint>
                </div>
                <div>
                  <label className={lbl}>Listener count</label>
                  <div className="mt-1.5 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setConfig((c) => ({ ...c, listenerCount: Math.max(0, c.listenerCount - 100) }))}
                      className="rounded-lg border border-stone-300 bg-white px-2.5 py-2.5 text-stone-600 hover:bg-stone-50"
                      aria-label="Decrease by 100"
                    >
                      <i className="ri-subtract-line" />
                    </button>
                    <input
                      type="number"
                      min={0}
                      value={config.listenerCount}
                      onChange={(e) => setConfig((c) => ({ ...c, listenerCount: Math.max(0, Number.parseInt(e.target.value, 10) || 0) }))}
                      className={`flex-1 text-center ${input}`}
                    />
                    <button
                      type="button"
                      onClick={() => setConfig((c) => ({ ...c, listenerCount: c.listenerCount + 100 }))}
                      className="rounded-lg border border-stone-300 bg-white px-2.5 py-2.5 text-stone-600 hover:bg-stone-50"
                      aria-label="Increase by 100"
                    >
                      <i className="ri-add-line" />
                    </button>
                  </div>
                  <FieldHint>Display-only. Shown next to &quot;listening&quot; — not wired to real analytics.</FieldHint>
                </div>
                <div>
                  <label className={lbl}>Broadcast status</label>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={config.isLive}
                    onClick={() => setConfig((c) => ({ ...c, isLive: !c.isLive }))}
                    className={`mt-1.5 inline-flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${
                      config.isLive
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100'
                        : 'border-stone-300 bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${config.isLive ? 'animate-pulse bg-emerald-500' : 'bg-stone-400'}`} />
                      {config.isLive ? 'Live — on air' : 'Offline'}
                    </span>
                    <span className="text-xs opacity-70">Click to toggle</span>
                  </button>
                  <FieldHint>Controls the pulsing green dot on the home card. Toggle off if you are not actively broadcasting.</FieldHint>
                </div>
              </div>
            </Section>

            {/* 2 · Stream URL */}
            <Section
              step="2"
              title="Stream URL"
              badge={
                streamType !== 'none' ? (
                  <span className={`ml-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${streamType === 'hls' || streamType === 'mp3' || streamType === 'icecast' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                    <i className={streamInfo.icon} />
                    {streamInfo.label}
                  </span>
                ) : null
              }
            >
              <div>
                <label className={lbl}>Audio stream URL</label>
                <input
                  value={config.streamUrl}
                  onChange={(e) => setConfig((c) => ({ ...c, streamUrl: e.target.value }))}
                  className={`mt-1.5 font-mono text-xs ${input}`}
                  placeholder="https://stream.example.com/radio.m3u8  —  or leave blank"
                />
                <FieldHint>
                  Optional. Paste an HLS (.m3u8), direct MP3, or Icecast URL to enable real audio streaming.
                  YouTube / SoundCloud links embed in the player instead. Leave blank to use the demo track metadata only.
                </FieldHint>
                {streamType === 'hls' && (
                  <p className="mt-2 text-xs font-medium text-emerald-700">
                    <i className="ri-checkbox-circle-line mr-1" />
                    HLS stream detected — the player will load this via the audio element when the user taps Play radio.
                  </p>
                )}
                {streamType === 'mp3' && (
                  <p className="mt-2 text-xs font-medium text-emerald-700">
                    <i className="ri-checkbox-circle-line mr-1" />
                    Direct audio URL detected — will stream immediately in the browser player.
                  </p>
                )}
                {streamType === 'icecast' && (
                  <p className="mt-2 text-xs font-medium text-blue-700">
                    <i className="ri-radio-line mr-1" />
                    Icecast / Shoutcast URL — will attempt to stream; CORS must be open on the server.
                  </p>
                )}
                {streamType === 'unknown' && (
                  <p className="mt-2 text-xs font-medium text-amber-700">
                    <i className="ri-question-line mr-1" />
                    URL type not recognised. The player will attempt to use it as a direct audio source.
                  </p>
                )}
              </div>
            </Section>

            {/* 3 · On-air */}
            <Section step="3" title="On-air (now playing)">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={autoDetectCurrentShow}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-900 hover:bg-sky-100"
                >
                  <i className="ri-time-line" /> Auto-detect from clock
                </button>
                <span className="self-center text-xs text-stone-400">or click &quot;Set as current&quot; on a schedule row below</span>
              </div>
              <div>
                <label className={lbl}>On-air title</label>
                <input
                  value={config.currentShowTitle}
                  onChange={(e) => setConfig((c) => ({ ...c, currentShowTitle: e.target.value }))}
                  className={`mt-1.5 ${input}`}
                  placeholder="African Vibes Live"
                />
                <FieldHint>
                  Main line under &quot;On air now&quot; on the home card. Must match a schedule row&apos;s Show name exactly for that row to be highlighted.
                </FieldHint>
              </div>
              <div>
                <label className={lbl}>On-air subtitle</label>
                <input
                  value={config.currentShowSubtitle}
                  onChange={(e) => setConfig((c) => ({ ...c, currentShowSubtitle: e.target.value }))}
                  className={`mt-1.5 ${input}`}
                  placeholder="Streaming live with curated energy from across the continent."
                />
                <FieldHint>Secondary line — mood, genre, or host info.</FieldHint>
              </div>
            </Section>

            {/* 4 · Demo track */}
            <Section step="4" title="Demo track art (Play radio)">
              <p className="-mt-1 text-sm text-stone-600">
                When a visitor taps <strong className="text-stone-800">Play radio</strong> and no stream URL is set, the player shows this metadata in the now-playing bar. Use a square image for best results.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={lbl}>Title</label>
                  <input value={config.trackTitle} onChange={(e) => setConfig((c) => ({ ...c, trackTitle: e.target.value }))} className={`mt-1.5 ${input}`} />
                </div>
                <div>
                  <label className={lbl}>Artist</label>
                  <input value={config.trackArtist} onChange={(e) => setConfig((c) => ({ ...c, trackArtist: e.target.value }))} className={`mt-1.5 ${input}`} />
                </div>
                <div>
                  <label className={lbl}>Album</label>
                  <input value={config.trackAlbum} onChange={(e) => setConfig((c) => ({ ...c, trackAlbum: e.target.value }))} className={`mt-1.5 ${input}`} />
                </div>
                <div>
                  <label className={lbl}>Duration</label>
                  <input value={config.trackDuration} onChange={(e) => setConfig((c) => ({ ...c, trackDuration: e.target.value }))} className={`mt-1.5 ${input}`} placeholder="2:48" />
                </div>
                <div className="sm:col-span-2">
                  <label className={lbl}>Cover image URL</label>
                  <div className="mt-1.5 flex gap-3">
                    <input
                      value={config.trackImage}
                      onChange={(e) => setConfig((c) => ({ ...c, trackImage: e.target.value }))}
                      className={`flex-1 font-mono text-xs ${input}`}
                      placeholder="https://…"
                    />
                    <img
                      src={previewImage || 'https://placehold.co/56x56/e7e5e4/a8a29e?text=Art'}
                      alt="Cover preview"
                      className="h-14 w-14 shrink-0 rounded-xl border border-stone-200 object-cover shadow-sm"
                      onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/56x56/e7e5e4/a8a29e?text=Art'; }}
                    />
                  </div>
                  <FieldHint>Preview updates ~0.6 s after you stop typing.</FieldHint>
                </div>
              </div>
            </Section>

            {/* Save bar */}
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                onClick={handleSaveClick}
                disabled={saving}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity disabled:opacity-50 ${
                  hasUnsavedChanges ? 'bg-[#6B3B18] hover:opacity-90' : 'bg-stone-500'
                }`}
              >
                {saving
                  ? <><i className="ri-loader-4-line animate-spin" /> Saving…</>
                  : <><i className="ri-save-3-line" /> Save radio settings{hasUnsavedChanges && ' *'}</>}
              </button>
              <button
                type="button"
                onClick={() => { setSaveBanner(null); onReload(); }}
                disabled={loading || saving}
                className="rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-sm font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-50"
              >
                Reload from server
              </button>
            </div>
          </div>

          {/* ── Preview + schedule column ── */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Live preview</p>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${config.isLive ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-200 text-stone-600'}`}>
                {config.isLive ? 'On air' : 'Offline'}
              </span>
            </div>
            <div className="overflow-hidden rounded-[1.75rem] border border-stone-200/70 bg-[radial-gradient(circle_at_top_right,_rgba(251,191,36,0.18),_transparent_30%),linear-gradient(135deg,_#1c1917_0%,_#44403c_42%,_#78350f_100%)] p-5 text-white shadow-[0_16px_60px_rgba(120,53,15,0.18)]">
              {/* Station header */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-amber-200/80">Live radio</p>
                  <h3 className="mt-1.5 truncate text-lg font-bold leading-tight">{config.stationName || '—'}</h3>
                  <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-stone-200">{config.tagline || '—'}</p>
                </div>
                <div className="shrink-0 rounded-full border border-white/15 bg-white/10 px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-wide text-amber-100">
                  {(config.listenerCount || 0).toLocaleString()} listening
                </div>
              </div>

              {/* Now on air */}
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <img
                    src={previewImage || 'https://placehold.co/80x80/292524/78716c?text=Art'}
                    alt=""
                    className="h-16 w-16 shrink-0 rounded-xl object-cover shadow-lg"
                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/80x80/292524/78716c?text=Art'; }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-amber-200/70">On air now</p>
                    <p className="mt-1 truncate text-base font-semibold">{currentShow || '—'}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-stone-200">{config.currentShowSubtitle || '—'}</p>
                    <div className="mt-2 flex items-center gap-1.5 text-xs">
                      {config.isLive ? (
                        <><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /><span className="text-emerald-200">On air — ready to play</span></>
                      ) : (
                        <><span className="h-1.5 w-1.5 rounded-full bg-stone-500" /><span className="text-stone-400">Station offline</span></>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-2 text-xs font-semibold text-stone-950">
                  <i className="ri-play-fill" /> Play radio
                </span>
                <button
                  type="button"
                  onClick={() => setPreviewScheduleOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-2 text-xs font-medium text-white hover:bg-white/15"
                >
                  <i className="ri-calendar-line" />
                  {previewScheduleOpen ? 'Hide schedule' : 'View schedule'}
                </button>
              </div>

              {/* Schedule preview */}
              {previewScheduleOpen && (
                <div className="mt-4 max-h-[200px] space-y-1.5 overflow-y-auto rounded-2xl border border-white/10 bg-white/10 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-100/85">Today&apos;s schedule</p>
                  {config.schedule.length === 0 ? (
                    <p className="text-xs text-stone-400">No rows yet.</p>
                  ) : (
                    config.schedule.map((item, index) => {
                      const isCurrent = item.show.trim() && item.show.trim() === currentShow.trim();
                      return (
                        <div
                          key={index}
                          className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-xs ${
                            isCurrent ? 'bg-amber-300/15 text-white' : 'bg-white/5 text-stone-200'
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium">{item.show || '(no show)'}</p>
                            <p className="text-[10px] text-stone-300">with {item.host || '—'}</p>
                          </div>
                          <div className="shrink-0 text-right">
                            <p>{item.time || '—'}</p>
                            {isCurrent && <p className="text-[10px] text-emerald-200">Current</p>}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Now playing bar */}
              <div className="mt-4 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-[10px] text-stone-300">
                <span className="font-medium text-amber-100/90">Now playing bar:</span>{' '}
                {config.trackTitle} · {config.trackArtist}
                {config.streamUrl && (
                  <span className="ml-2 text-sky-300/80">· stream set</span>
                )}
              </div>
            </div>

            {/* Stream status tile */}
            {streamType !== 'none' && (
              <div className={`rounded-2xl border p-4 text-sm ${streamType === 'hls' || streamType === 'mp3' || streamType === 'icecast' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                <p className="font-semibold"><i className={`${streamInfo.icon} mr-1.5`} />{streamInfo.label}</p>
                <p className="mt-1 break-all font-mono text-xs opacity-75">{config.streamUrl}</p>
              </div>
            )}

            {/* ── Schedule editor (right column) ── */}
            <div className="rounded-2xl border border-stone-200 bg-stone-50/60 p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-stone-800">Day schedule</p>
                  <p className="mt-0.5 text-xs text-stone-500">Shown when visitors tap <strong className="text-stone-700">View schedule</strong>. Up to 50 rows.</p>
                </div>
                <span className="shrink-0 rounded-full bg-stone-200 px-2 py-0.5 text-xs font-medium text-stone-600">
                  {config.schedule.length} row{config.schedule.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="mb-3 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={addScheduleRow}
                  className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-950 hover:bg-amber-100"
                >
                  <i className="ri-add-line" /> Add row
                </button>
                <button
                  type="button"
                  onClick={autoDetectCurrentShow}
                  className="inline-flex items-center gap-1 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-900 hover:bg-sky-100"
                >
                  <i className="ri-time-line" /> Auto-detect current
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!window.confirm('Reset the entire schedule to defaults? Your current rows will be lost.')) return;
                    setConfig((c) => ({ ...c, schedule: DEFAULT_RADIO_CONFIG.schedule }));
                  }}
                  className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50"
                >
                  <i className="ri-refresh-line" /> Reset
                </button>
              </div>

              <div className="space-y-2">
                {config.schedule.length === 0 && (
                  <p className="rounded-xl border border-dashed border-stone-300 py-6 text-center text-xs text-stone-500">
                    No rows yet — click <strong>Add row</strong> to build the schedule.
                  </p>
                )}
                {config.schedule.map((row, i) => {
                  const isCurrentSlot = row.show.trim() && row.show.trim() === config.currentShowTitle.trim();
                  return (
                    <div
                      key={i}
                      className={`rounded-xl border p-3 ${
                        isCurrentSlot
                          ? 'border-emerald-300 bg-emerald-50/70'
                          : 'border-stone-200 bg-white'
                      }`}
                    >
                      {/* Row header */}
                      <div className="mb-2.5 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] font-semibold text-stone-400">#{i + 1}</span>
                          {isCurrentSlot && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                              <i className="ri-broadcast-line" /> On air
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            title="Set as current on-air show"
                            onClick={() => setAsCurrentShow(row)}
                            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${
                              isCurrentSlot
                                ? 'border-emerald-300 bg-emerald-100 text-emerald-800 cursor-default'
                                : 'border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100'
                            }`}
                          >
                            <i className="ri-broadcast-line" />
                            {isCurrentSlot ? 'On air' : 'Set current'}
                          </button>
                          <button type="button" title="Move up" onClick={() => moveScheduleRow(i, -1)} disabled={i === 0}
                            className="rounded-md border border-stone-200 bg-white p-1 text-stone-500 hover:bg-stone-50 disabled:opacity-30">
                            <i className="ri-arrow-up-s-line text-sm" />
                          </button>
                          <button type="button" title="Move down" onClick={() => moveScheduleRow(i, 1)} disabled={i === config.schedule.length - 1}
                            className="rounded-md border border-stone-200 bg-white p-1 text-stone-500 hover:bg-stone-50 disabled:opacity-30">
                            <i className="ri-arrow-down-s-line text-sm" />
                          </button>
                          <button type="button" title="Duplicate" onClick={() => duplicateScheduleRow(i)}
                            className="rounded-md border border-stone-200 bg-white p-1 text-stone-500 hover:bg-stone-50">
                            <i className="ri-file-copy-line text-sm" />
                          </button>
                          <button type="button" title="Remove" onClick={() => removeScheduleRow(i)}
                            className="rounded-md border border-red-200 bg-red-50 p-1 text-red-600 hover:bg-red-100">
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      </div>

                      {/* Fields */}
                      <div className="grid gap-2 sm:grid-cols-[1fr_2fr_1.5fr]">
                        <div>
                          <label className="block text-[10px] font-semibold uppercase tracking-wide text-stone-500">Time</label>
                          <input
                            value={row.time}
                            onChange={(e) => updateScheduleRow(i, { time: e.target.value })}
                            className={`mt-1 ${input}`}
                            placeholder="06:00 - 09:00"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-semibold uppercase tracking-wide text-stone-500">Show</label>
                          <input
                            value={row.show}
                            onChange={(e) => updateScheduleRow(i, { show: e.target.value })}
                            className={`mt-1 ${input}`}
                            placeholder="Morning Drive"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-semibold uppercase tracking-wide text-stone-500">Host</label>
                          <input
                            value={row.host}
                            onChange={(e) => updateScheduleRow(i, { host: e.target.value })}
                            className={`mt-1 ${input}`}
                            placeholder="DJ Name"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
